/**
 * A press that is with the scheduler (1.5.15). Finish, Arrived, Confirm by phone, Move and Cancel do not change
 * anything themselves: each puts a request in a queue and the scheduler (the Power Automate flows) answers on its
 * next run, a few minutes later. Until then the request is open, and the screen says so in one way everywhere.
 *
 * The server says which requests are open for a visit (so it reads the same after a reload and on another device);
 * the device adds the press it has just made, until the first read after the press.
 */

export type PressKind = 'finish' | 'arrived' | 'confirm' | 'move' | 'cancel' | 'other';

/** An open request for one visit, as Today reads it. */
export interface PendingPress {
  queue: 'q1' | 'q2' | 'q3';
  kind: PressKind;
  /** The queue's own OperationType, exactly as stored. */
  operation: string;
  transactionId: string;
  /** When it was pressed (the request's SubmittedDateTime). */
  at: string;
}

/** The last close-out of a visit, when the scheduler refused it: the visit is closable again, with these words. */
export interface RefusedCloseout {
  transactionId: string;
  at: string;
  processingStatus: string;
  outcomeCode: string | null;
  /** The scheduler's own words — shown verbatim. */
  outcomeMessage: string | null;
}

/** A request this device sent (the outcome tracker's line), as much of it as the merge needs. */
export interface DevicePress {
  queue?: 'q1' | 'q2' | 'q3';
  operation: string;
  transactionId: string;
  appointmentId: string | null | undefined;
  open: boolean;
  submittedAt?: string;
  /** When this device sent it (ms). */
  trackedAt: number;
}

const KIND_OF: Record<string, PressKind> = {
  'Record Completion': 'finish',
  'Start Service': 'arrived',
  'Record Tenant Confirmation Response': 'confirm',
  'Reschedule Appointment': 'move',
  'Cancel Appointment': 'cancel',
};

/** The press behind a queue's OperationType (anything else — a hold, a no-show, a reopen — is `other`). */
export function pressKindOf(operation: string): PressKind {
  return KIND_OF[operation] ?? 'other';
}

/**
 * The open requests for one visit: the server's list, plus what this device has sent and the server has not been
 * read since (`readAt`, ms — when the visit was last read; 0 where no read carries the server's list, so the
 * device's own tracker is all there is). Once the day has been read after the press, the server alone is the truth:
 * a request it no longer lists has been answered.
 */
export function openPresses(appointmentId: string | null | undefined, server: readonly PendingPress[] | null | undefined, device: readonly DevicePress[], readAt = 0, nowMs?: number): PendingPress[] {
  if (!appointmentId) return [];
  // a request nobody has answered for half an hour no longer holds the buttons back (`stalePress` says so instead)
  const out = (server ?? []).filter((p) => nowMs === undefined || !isStalePress(p, nowMs));
  for (const d of device) {
    if (!d.open || d.appointmentId !== appointmentId) continue;
    if (d.trackedAt < readAt || out.some((p) => p.transactionId === d.transactionId)) continue;
    out.push({ queue: d.queue ?? 'q1', kind: pressKindOf(d.operation), operation: d.operation, transactionId: d.transactionId, at: d.submittedAt || new Date(d.trackedAt).toISOString() });
  }
  return out;
}

/**
 * "A few minutes" has a limit: the scheduler runs every five minutes, so a request still open after this long is
 * stuck (the flow is off, or it failed). The card then stops saying "in a few minutes", the buttons come back, and
 * the server takes a new close-out instead of answering "already sent".
 */
export const PRESS_STALE_MINUTES = 30;
export const isStalePress = (p: Pick<PendingPress, 'at'>, nowMs: number): boolean => nowMs - Date.parse(p.at) > PRESS_STALE_MINUTES * 60_000;
/** The oldest request of a visit that has waited too long, if any. */
export const stalePress = (server: readonly PendingPress[] | null | undefined, nowMs: number): PendingPress | null => (server ?? []).find((p) => isStalePress(p, nowMs)) ?? null;

/** The close-out that is waiting for the scheduler, if there is one. */
export function closingOut(presses: readonly PendingPress[]): PendingPress | null {
  return presses.find((p) => p.kind === 'finish') ?? null;
}

const OPEN_VISIT = new Set(['Scheduled', 'In Progress']);

/** Still to close out: the visit is open and no close-out is waiting (a pending close-out is not "not closed"). */
export function awaitsCloseOut(v: { status: string }, presses: readonly PendingPress[]): boolean {
  return OPEN_VISIT.has(v.status) && !closingOut(presses);
}

/**
 * 1.5.22 — "late" on Today's "visits to close out" card. The minutes are the close-out reminder's own setting
 * (Settings → Emails: 30, 60, 90, 120 after the visit's scheduled end); with the reminder OFF, or nothing set, the
 * hour — which is how the server's own "Pending close-out" list counts it (api/src/reads/index.ts).
 */
export const CLOSEOUT_LATE_SETTING = 'EMAIL_CLOSEOUT_REMINDER_MINUTES';
export function closeoutLateAfter(value: string | null | undefined): number {
  const n = Number(String(value ?? '').trim());
  return Number.isFinite(n) && n > 0 && n <= 24 * 60 ? n : 60;
}
/** How many whole minutes past its scheduled end a visit is, once that is `after` or more; otherwise null. */
export function closeoutLate(endMinutes: number | null | undefined, nowMinutes: number, after: number): number | null {
  if (endMinutes === null || endMinutes === undefined || !Number.isFinite(endMinutes)) return null;
  const late = Math.floor(nowMinutes - endMinutes);
  return late >= after ? late : null;
}
